import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {debounceTime, filter, switchMap, takeUntil} from 'rxjs/operators';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth/services/auth.service';
import { Menu, TicketStatus } from 'src/app/shared/models/common';
import { PermissionService } from 'src/app/utils/permission.service';
import { TicketService } from 'src/app/pages/support/services/ticket.service';
import { InAppNotificationService } from 'src/app/shared/services/in-app-notification.service';
import { RoleService } from '../../shared/services/role.service';
import { UserService } from '../../shared/services/user.service';
import { Unsubscribe } from 'src/app/utils/unsubscribe.service';
import { getSideMenus } from './menu';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NotificationService } from 'src/app/utils/notification.service';
import { IndexedDBService } from 'src/app/indexDB/indexedDB.service';
import { AppLogoService } from 'src/app/shared/services/app-logo.service';
import { TranslationHelperService } from 'src/app/utils/translation-helper.service';
import { MatDialog } from '@angular/material/dialog';
import { ProceedModalComponent } from 'src/app/shared/modals/proceed-modal/proceed-modal.component';

interface SideNavToggle {
  screenWidth: number,
  collapsed: boolean
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
})
export class SidebarComponent extends Unsubscribe implements OnInit, OnDestroy {
  logo$: Observable<string>;

  private subscription: Subscription = new Subscription();
  private showReports: boolean;
  @Output() toggleSideNav: EventEmitter<SideNavToggle> = new EventEmitter()
  // mobile slide-in drawer state, driven from MainComponent (mobile only)
  @Input() mobileOpen: boolean = false
  @Output() mobileNavClose: EventEmitter<void> = new EventEmitter()
  collapsed: boolean = true
  screenWidth = 0

  public menus: Menu[] = [];
  public AllMenu: Menu[] = getSideMenus();
  public franchiseeDetails: any = {};
  public u: any = {};
  private hour: number;
  private year: any;
  public activeLink: string = '/';
  public badgeCounts: { [link: string]: number } = {};

  constructor(
    public roleService: RoleService,
    public route: ActivatedRoute,
    private router: Router,
    private _auth: AuthService,
    private _user: UserService,
    private permissionsService: PermissionService,
    private sanitizer: DomSanitizer,
    private toast: NotificationService,
    private iDBService: IndexedDBService,
    private appLogoService: AppLogoService,
    private translateHelper: TranslationHelperService,
    private dialog: MatDialog,
    private ticketService: TicketService,
    private inAppNotificationService: InAppNotificationService
  ) {
    super()
    this.activeLink = `/${location.pathname.split('/')[1].split('?')[0]}`
    this.evaluateUserMenu();
    this.logo$ = this.appLogoService.logo$;
  }
  /**
   * iterate through menu and submenu
   * compare list of (menu | submenu) permissions against client permissions
   * return filtered menu;
   */
  public evaluateUserMenu() {
    // this.subscription.add(
      this.permissionsService.UsersPermissions$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((perms) => {
        if (!Object.keys(perms).length) return;
        const allMenu: Menu[] = JSON.parse(JSON.stringify(this.AllMenu));
        this.menus = allMenu.reduce((acc: Menu[], menu: Menu) => {
          if (menu.children) {
            menu.children = menu.children.filter((subMenu) =>
              this.permissionsService.hasPermissionList(subMenu.perm)
            );
            if (!menu.children.length) return acc;
            acc.push(menu);
            return acc;
          }
          if (
            !menu.children &&
            !this.permissionsService.hasPermissionList(menu.perm)
          )
            return acc;
          acc.push(menu);
          return acc;
        }, []);
      })
    // );
  }

  

  setViewedUpdate(id) {
    this._user.setViewedUpdateToTrue(id).subscribe((data) => {
      this.u.viewedUpdate = true;
      localStorage.setItem('user', JSON.stringify(this.u));
    });
  }

  logout(): void {
    const dialogRef = this.dialog.open(ProceedModalComponent, {
      disableClose: true,
      data: {
        message: $localize`:@@logout_confirmation:Are you sure you want to logout?`,
        proceedVal: {
          name: $localize`:@@logout:Logout`,
          class: 'p-3 rounded-xl text-white bg-error/90 hover:bg-error w-[150px]',
        },
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this._auth.logout().subscribe({
        next: () => {
          this.toast.success(this.translateHelper.translateBackendMessage("User logged out successfully"));
          this.iDBService.deleteDatabase();
          location.href = '/login';
        },
        error: () => {
          localStorage.clear();
          this.toast.success(this.translateHelper.translateBackendMessage("User logged out successfully"));
          location.href = '/login';
        },
      });
    });
  }

  ngOnInit(): void {
    this.listenOnWindowWidthChanged()

    this.hour = new Date().getHours();
    this.year = new Date().getFullYear();
    this.showReports =
      this.hour < 8 || (this.hour >= 10 && this.hour <= 16) || this.hour >= 20;

      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe((event:any) => {
        this.activeLink = `/${event.url.split('/')[1].split('?')[0]}`;
      });

    this.loadBadgeCounts();
  }

  private loadBadgeCounts(): void {
    const user = this._auth.getuser;

    this.ticketService.getAllTicket({
      page: 0,
      size: 1,
      loanOfficerId: user?.id || 0,
      status: TicketStatus.OPEN,
      sortOrder: 'DESC',
      sortField: 'createdAt',
    }).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: res => { this.badgeCounts['/support'] = res.data?.totalElements || 0; },
      error: () => {}
    });

    this.inAppNotificationService.stats$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(stats => { this.badgeCounts['/updates'] = stats?.unread || 0; });
  }

  listenOnWindowWidthChanged() {
    this.screenWidth = window.innerWidth
    if(this.screenWidth <= 768) {
      this.collapsed = false
      this.toggleSideNav.emit({ screenWidth: this.screenWidth, collapsed: this.collapsed })
    } else {
      this.collapsed = true
      this.toggleSideNav.emit({ screenWidth: this.screenWidth, collapsed: this.collapsed })
    }
    
    fromEvent(window, 'resize').pipe(takeUntil(this.unsubscribe$), debounceTime(200)).subscribe(ev => {
      if(window.innerWidth <= 768) {
        this.collapsed = false
        this.toggleSideNav.emit({ screenWidth: window.innerWidth, collapsed: this.collapsed })
      } else {
        this.collapsed = true
        this.toggleSideNav.emit({ screenWidth: window.innerWidth, collapsed: this.collapsed })
      }
    })
  }

  toggleCollapse() {
    this.collapsed = !this.collapsed
    this.toggleSideNav.emit({ screenWidth: this.screenWidth, collapsed: this.collapsed })
  }

  closeNav() {
    this.collapsed = false
    this.toggleSideNav.emit({ screenWidth: this.screenWidth, collapsed: this.collapsed })
  }

  // close the mobile slide-in drawer (backdrop / close button / nav item tap)
  closeMobile() {
    this.mobileNavClose.emit()
  }

  renderIcon(icon: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(icon);
  }

}
